import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardBody, Button, Progress } from '@heroui/react';
import { ArrowRight, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Guide } from '@/services/apis/guides';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/utils/cn';

interface InProgressGuideCardProps {
  guide: Guide;
  progress: {
    id: string;
    guideId: string;
    currentStepId: string | null;
    completedStepIds: string[];
    quizScores: Record<string, number>;
    isCompleted: boolean;
    completedAt: string | null;
    startTime: string | null;
    language: string;
    createdAt: string;
    updatedAt: string;
  };
  language?: string;
}

const InProgressGuideCard: React.FC<InProgressGuideCardProps> = ({ guide, progress, language = 'en' }) => {
  const { t } = useTranslation('home');

  const translation = guide.translations?.find((t) => t.language === language) || guide.translations?.[0];
  const title = translation?.title || 'Untitled Guide';
  const description = translation?.description || '';

  const totalSteps = guide.steps?.length || 0;
  const completedSteps = progress.completedStepIds.length;
  const stepsRemaining = totalSteps - completedSteps;
  const progressPercentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      whileHover={{ y: -4 }}
    >
      <Card className="h-full hover:shadow-lg transition-shadow">
        <CardBody className="p-6">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="mb-4">
              <h3 title={title} className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                {title}
              </h3>
              {description && (
                <p title={description} className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {description}
                </p>
              )}
            </div>

            {/* Progress Section */}
            <div className="mb-4 flex-1">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('sections.inProgress.stepsLeft', { count: stepsRemaining })}
                  </span>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {completedSteps}/{totalSteps}
                </span>
              </div>
              <Progress value={progressPercentage} className="w-full" color="primary" size="sm" aria-label="Progress" />
            </div>

            {/* Continue Button */}
            <Button
              color="primary"
              variant="solid"
              className={cn(
                'w-full font-semibold',
                'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600',
                'hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700',
                'text-white border-0',
              )}
              endContent={<ArrowRight className="w-4 h-4" />}
              as={Link}
              to={`/guides/${guide.id}`}
            >
              Continue
            </Button>
          </div>
        </CardBody>
      </Card>
    </motion.div>
  );
};

export default InProgressGuideCard;
